import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  CreateSuggestionRequest,
  Suggestion,
  SuggestionCategory,
  SuggestionSeverity,
  SuggestionScreenshot,
} from '@om/shared-types';
import { Camera, Loader2, Send, Trash2, Upload } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { uploadService } from '@/lib/api/upload.service';
import { captureFeedbackScreenshot } from '@/lib/feedback/screenshot';
import { suggestionService } from '@/lib/api/suggestion.service';

const CATEGORY_VALUES: SuggestionCategory[] = ['general', 'bug', 'feature', 'ux', 'performance', 'other'];
const SEVERITY_VALUES: SuggestionSeverity[] = ['low', 'medium', 'high', 'critical'];

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

interface FeedbackNavigationState {
  prefilledScreenshots?: SuggestionScreenshot[];
  prefillErrorMessage?: string;
}

export const WebsiteFeedbackPage: React.FC = () => {
  const { t } = useTranslation(['profile']);
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<SuggestionCategory>('general');
  const [severity, setSeverity] = useState<SuggestionSeverity>('medium');
  const [screenshots, setScreenshots] = useState<SuggestionScreenshot[]>([]);
  const [recentSuggestions, setRecentSuggestions] = useState<Suggestion[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prefillAppliedRef = useRef(false);

  useEffect(() => {
    void loadRecentSuggestions();
  }, []);

  useEffect(() => {
    if (prefillAppliedRef.current) {
      return;
    }

    prefillAppliedRef.current = true;

    const state = (location.state as FeedbackNavigationState | null) ?? null;
    if (!state) {
      return;
    }

    const prefilledScreenshots = state.prefilledScreenshots;
    if (Array.isArray(prefilledScreenshots) && prefilledScreenshots.length > 0) {
      setScreenshots((prev) => [...prev, ...prefilledScreenshots]);
      setSuccessMessage('Screenshot attached to the feedback form.');
      return;
    }

    if (typeof state.prefillErrorMessage === 'string' && state.prefillErrorMessage.trim().length > 0) {
      setErrorMessage(state.prefillErrorMessage);
    }
  }, [location.state]);

  const loadRecentSuggestions = async (): Promise<void> => {
    try {
      const result = await suggestionService.getMySuggestions(5, 0);
      setRecentSuggestions(result.data);
    } catch {
      // Non-blocking: feedback form still works even if recent history cannot be loaded.
      setRecentSuggestions([]);
    }
  };

  const appendScreenshot = (entry: SuggestionScreenshot): void => {
    setScreenshots((prev) => [...prev, entry]);
  };

  const uploadFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setUploading(true);

    try {
      for (const file of files) {
        const uploaded = await uploadService.uploadImage(file);
        appendScreenshot({
          url: uploaded.url,
          filename: uploaded.filename,
          original_name: file.name,
          size: file.size,
          mime_type: file.type,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to upload screenshot';
      setErrorMessage(message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    await uploadFiles(files);
  };

  const handleCaptureScreenshot = async (): Promise<void> => {
    setErrorMessage(null);
    setSuccessMessage(null);

    setCapturing(true);

    try {
      const file = await captureFeedbackScreenshot();
      await uploadFiles([file]);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to capture screenshot';
      setErrorMessage(message);
    } finally {
      setCapturing(false);
    }
  };

  const removeScreenshot = async (index: number): Promise<void> => {
    const screenshot = screenshots[index];
    if (!screenshot) {
      return;
    }

    if (screenshot.filename) {
      try {
        await uploadService.deleteImage(screenshot.filename);
      } catch {
        // Ignore delete errors to avoid blocking local list cleanup.
      }
    }

    setScreenshots((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const resetForm = (): void => {
    setTitle('');
    setMessage('');
    setCategory('general');
    setSeverity('medium');
    setScreenshots([]);
  };

  const submitSuggestion = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!title.trim()) {
      setErrorMessage('Please provide a short title for your feedback.');
      return;
    }

    if (!message.trim()) {
      setErrorMessage('Please describe your feedback.');
      return;
    }

    setSubmitting(true);

    try {
      const payload: CreateSuggestionRequest = {
        title: title.trim(),
        message: message.trim(),
        category,
        severity,
        screenshots,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      };

      await suggestionService.submitSuggestion(payload);
      setSuccessMessage('Thanks for your feedback. We have recorded your suggestion.');
      resetForm();
      await loadRecentSuggestions();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to submit feedback';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />

      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{t('profile:feedback.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('profile:feedback.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('profile:feedback.formTitle')}</CardTitle>
            <CardDescription>
              {t('profile:feedback.formDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submitSuggestion}>
              <div className="space-y-2">
                <Label htmlFor="suggestion-title">{t('profile:feedback.fields.title')}</Label>
                <Input
                  id="suggestion-title"
                  value={title}
                  maxLength={200}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={t('profile:feedback.fields.titlePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="suggestion-category">{t('profile:feedback.fields.category')}</Label>
                  <select
                    id="suggestion-category"
                    aria-label={t('profile:feedback.fields.categoryAriaLabel')}
                    value={category}
                    onChange={(event) => setCategory(event.target.value as SuggestionCategory)}
                    className="h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  >
                    {CATEGORY_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {t(`profile:feedback.categories.${value}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="suggestion-severity">{t('profile:feedback.fields.severity')}</Label>
                  <select
                    id="suggestion-severity"
                    aria-label={t('profile:feedback.fields.severityAriaLabel')}
                    value={severity}
                    onChange={(event) => setSeverity(event.target.value as SuggestionSeverity)}
                    className="h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  >
                    {SEVERITY_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {t(`profile:feedback.severities.${value}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="suggestion-message">{t('profile:feedback.fields.details')}</Label>
                <Textarea
                  id="suggestion-message"
                  value={message}
                  maxLength={5000}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={t('profile:feedback.fields.detailsPlaceholder')}
                  className="min-h-[140px]"
                />
                <p className="text-xs text-muted-foreground text-right">{message.length}/5000</p>
              </div>

              <div className="space-y-2">
                <Label>{t('profile:feedback.fields.screenshots')}</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleCaptureScreenshot()}
                    disabled={capturing || uploading || screenshots.length >= 10}
                  >
                    {capturing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}
                    {t('profile:feedback.actions.captureScreenshot')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || capturing || screenshots.length >= 10}
                  >
                    {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                    {t('profile:feedback.actions.uploadFiles')}
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  aria-label={t('profile:feedback.fields.uploadAriaLabel')}
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    void handleFileInput(event);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile:feedback.fields.screenshotsHint')}
                </p>
              </div>

              {screenshots.length > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {screenshots.map((screenshot, index) => (
                    <div key={`${screenshot.url}-${index}`} className="relative overflow-hidden rounded-md border">
                      <img
                        src={screenshot.url}
                        alt={t('profile:feedback.fields.screenshotAlt', { number: index + 1 })}
                        className="h-40 w-full object-cover"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-2 rounded bg-black/65 p-1 text-white"
                        onClick={() => {
                          void removeScreenshot(index);
                        }}
                        aria-label={t('profile:feedback.fields.removeScreenshotAriaLabel')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {errorMessage && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {successMessage}
                </div>
              )}

              <div className="flex justify-end">
                <Button type="submit" disabled={submitting || uploading || capturing}>
                  {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                  {t('profile:feedback.actions.submit')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('profile:feedback.autoMetadata.title')}</CardTitle>
              <CardDescription>{t('profile:feedback.autoMetadata.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <p className="font-medium">{t('profile:feedback.autoMetadata.pageUrl')}</p>
                <p className="break-all text-muted-foreground">{window.location.href}</p>
              </div>
              <div>
                <p className="font-medium">{t('profile:feedback.autoMetadata.browser')}</p>
                <p className="break-words text-muted-foreground">{navigator.userAgent}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('profile:feedback.recentSubmissions.title')}</CardTitle>
              <CardDescription>{t('profile:feedback.recentSubmissions.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {recentSuggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('profile:feedback.recentSubmissions.empty')}</p>
              ) : (
                <ul className="space-y-3">
                  {recentSuggestions.map((suggestion) => (
                    <li key={suggestion.id} className="rounded-md border p-3">
                      <p className="text-sm font-semibold">{suggestion.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{formatDate(suggestion.created_at)}</p>
                      <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{suggestion.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

export default WebsiteFeedbackPage;
